import React, { useEffect, useState } from "react";
import axios from "axios";
import produce from "immer";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import LoadingButton from "@mui/lab/LoadingButton";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

import Loader from "../loader/Loader";
import { fieldMappingObj } from "../../AddAddressModal";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const fieldOrder = [
  "address_1",
  "address_2",
  "city",
  "state",
  "zip",
  "county",
  "country",
  "formatted_address",
  "latitude",
  "longitude",
];

export default function FieldLabels({ orgId, zohoApiKey }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingRows, setSettingRows] = useState([]);
  const [labels, setLabels] = useState({});
  const [snackbar, setSnackbar] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.request({
          url: `${process.env.REACT_APP_ADMIN_SERVER_URL}/db/planetscale/clients/settings`,
          method: "GET",
          headers: {
            orgid: orgId, // Org ID
            apikey: zohoApiKey, // API KEy
            connname: "easyaddressautocomplete", // Conn Name
          },
        });

        const parsedRows = (response.data?.data || []).map((row) => ({
          ...row,
          setting_schema: JSON.parse(row.setting_schema),
        }));
        setSettingRows(parsedRows);

        //current label of each field: taken from the first block that has it,
        //otherwise the default
        const initialLabels = {};
        fieldOrder.forEach((fieldKey) => {
          let currentLabel = null;
          parsedRows.some((row) =>
            (row.setting_schema.blocks || []).some((block) => {
              if (block.fieldMapping?.[fieldKey]?.label) {
                currentLabel = block.fieldMapping[fieldKey].label;
                return true;
              }
              return false;
            })
          );
          initialLabels[fieldKey] =
            currentLabel || fieldMappingObj[fieldKey]?.label || fieldKey;
        });
        setLabels(initialLabels);
      } catch (err) {
        // alert(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, zohoApiKey]);

  const handleSave = async () => {
    if (fieldOrder.some((fieldKey) => !labels[fieldKey]?.trim())) {
      setSnackbar({
        severity: "error",
        message: "Field labels cannot be empty!",
      });
      return;
    }

    setSaving(true);
    try {
      //apply the labels to every block of every module setting
      const updatedRows = settingRows.map((row) => ({
        ...row,
        setting_schema: {
          ...row.setting_schema,
          blocks: (row.setting_schema.blocks || []).map((block) => ({
            ...block,
            fieldMapping: Object.fromEntries(
              Object.entries(block.fieldMapping).map(([key, entry]) => [
                key,
                labels[key] ? { ...entry, label: labels[key].trim() } : entry,
              ])
            ),
          })),
        },
      }));

      await Promise.all(
        updatedRows.map((row) =>
          axios.request({
            url: `${process.env.REACT_APP_ADMIN_SERVER_URL}/db/planetscale/clients/settings`,
            method: "POST",
            data: {
              settingId: row.setting_id,
              settingSchema: row.setting_schema,
              showInRelatedList: false,
              moduleApiName:
                row.setting_schema?.moduleName?.api_name || row.module_name,
              settingType: "button",
            },
            headers: {
              orgid: orgId, // Org ID
              apikey: zohoApiKey, // API KEy
              connname: "easyaddressautocomplete", // Conn Name
            },
          })
        )
      );

      setSettingRows(updatedRows);
      setSnackbar({
        severity: "success",
        message: "Field labels updated for all modules",
      });
    } catch (err) {
      setSnackbar({ severity: "error", message: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <Box sx={{ maxWidth: "1100px", mx: "auto", px: 3, py: 4 }}>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={(event, reason) => {
          if (reason === "clickaway") return;
          setSnackbar(null);
        }}
      >
        <Alert
          onClose={() => setSnackbar(null)}
          severity={snackbar?.severity || "success"}
          sx={{ width: "100%" }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>

      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        Field Label Modification
      </Typography>
      <Typography sx={{ mb: 3, color: "rgba(0, 0, 0, 0.6)" }}>
        Customize the field names shown in the address popup. For example,
        rename "City" to "Suburb" or "Zip" to "Postcode". These labels apply to
        every address block of every module setting.
      </Typography>

      {settingRows.length === 0 ? (
        <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
          No module settings found. Please create module settings from the
          Settings tab first.
        </Typography>
      ) : (
        <>
          <Grid container spacing={2}>
            {fieldOrder.map((fieldKey) => {
              const defaultLabel = fieldMappingObj[fieldKey]?.label || fieldKey;
              return (
                <Grid item xs={12} sm={6} md={4} key={fieldKey}>
                  <TextField
                    fullWidth
                    size="small"
                    label={defaultLabel}
                    value={labels[fieldKey] ?? ""}
                    error={!labels[fieldKey]?.trim()}
                    inputProps={{ maxLength: 40 }}
                    onChange={(e) => {
                      const newLabel = e.target.value;
                      setLabels(
                        produce((draft) => {
                          draft[fieldKey] = newLabel;
                        })
                      );
                    }}
                    InputProps={{
                      endAdornment: labels[fieldKey] !== defaultLabel && (
                        <IconButton
                          size="small"
                          title="Reset to default label"
                          onClick={() =>
                            setLabels(
                              produce((draft) => {
                                draft[fieldKey] = defaultLabel;
                              })
                            )
                          }
                        >
                          <RestartAltIcon fontSize="inherit" />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <LoadingButton
              variant="contained"
              loading={saving}
              onClick={handleSave}
            >
              <span>Save</span>
            </LoadingButton>
          </Box>
        </>
      )}
    </Box>
  );
}
